/**
 * DataFreshnessBanner — answers the #1 trust question on every dashboard
 * view: "is this data current?"
 *
 * Three states, in priority order:
 *   1. A connected integration's last sync FAILED → amber banner with a
 *      link to settings. Without this, a stale cash-flow number looks
 *      identical to a fresh one and owners quietly fall back to QuickBooks.
 *   2. An integration is connected but has never completed a first sync →
 *      "Syncing your data…" so the first-run dashboard isn't a silent blank.
 *   3. All healthy → a one-line "Data updated Xm ago" whisper. Hidden when
 *      nothing is connected (onboarding owns that state).
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listIntegrations, findIntegrationDef } from '../../lib/integrations';

function relTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * mode="alert": states 1–2 only (sync failed / first sync running) — lives in
 * the banner slot. mode="status": state 3 only, the one-line freshness
 * whisper rendered beneath the slot so it never competes with a banner.
 */
export function DataFreshnessBanner({ mode }: { mode: 'alert' | 'status' }) {
  const { data: integrations } = useQuery({
    queryKey: ['integrations', 'freshness'],
    queryFn: listIntegrations,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const connected = (integrations ?? []).filter(
    (i) => i.status === 'active' || i.status === 'connected',
  );
  if (connected.length === 0) return null;

  // A refusal that belongs to Waypoint's own Google Cloud project (an API not
  // enabled, or Business Profile access not yet approved) is not something the
  // customer can fix, so it does not get a "Fix in Settings" banner on every
  // page. The source's own card still shows the reason.
  const projectLevel = /for this project/i;
  const failed = connected.filter(
    (i) => i.last_sync_status && /^(error|failed)/i.test(i.last_sync_status) && !projectLevel.test(i.last_sync_status),
  );
  // Companion rows sync under their parent mailbox row — Google sub-scopes
  // (calendar/gsc/youtube/gbp) under gmail, outlook_calendar under outlook —
  // and their own integration row may never get its own last_synced_at stamp.
  // If the parent has synced, treat them as covered — otherwise the "Syncing…"
  // banner sticks forever even though data is landing.
  const COVERED_BY_GMAIL = ['calendar', 'gsc', 'youtube', 'gbp'];
  const COVERED_BY_OUTLOOK = ['outlook_calendar', 'teams'];
  const gmailSynced = connected.some((i) => i.provider === 'gmail' && i.last_synced_at);
  const outlookSynced = connected.some((i) => i.provider === 'outlook' && i.last_synced_at);
  const neverSynced = connected.filter(
    (i) =>
      !i.last_synced_at &&
      !(gmailSynced && COVERED_BY_GMAIL.includes(i.provider)) &&
      !(outlookSynced && COVERED_BY_OUTLOOK.includes(i.provider)),
  );
  const synced = connected.filter((i) => i.last_synced_at);

  // State 1: a sync is failing — say so loudly.
  if (failed.length > 0) {
    if (mode === 'status') return null;
    const names = failed
      .map((f) => findIntegrationDef(f.provider)?.name ?? f.provider)
      .slice(0, 2)
      .join(', ');
    return (
      <div className="flex items-center justify-between gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5">
        <span className="text-2xs text-amber-300">
          <span className="font-semibold">{names}</span> sync failed — numbers from{' '}
          {failed.length === 1 ? 'this source' : 'these sources'} may be stale.
        </span>
        <Link
          to="/dashboard/settings?tab=integrations"
          className="shrink-0 text-2xs font-semibold text-amber-200 underline underline-offset-2 hover:brightness-110"
        >
          Fix in Settings
        </Link>
      </div>
    );
  }

  // State 2: first sync still running — reassure instead of showing blanks.
  if (neverSynced.length > 0) {
    if (mode === 'status') return null;
    const names = neverSynced
      .map((f) => findIntegrationDef(f.provider)?.name ?? f.provider)
      .slice(0, 2)
      .join(', ');
    return (
      <div className="flex items-center gap-2 border-b border-blue-500/20 bg-blue-500/5 px-4 py-1.5">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-400" />
        <span className="text-2xs text-ink-muted">
          Syncing {names} — your first sync usually takes a few minutes. Data will
          appear here as it lands.
        </span>
      </div>
    );
  }

  // State 3: healthy — quiet freshness whisper.
  if (mode === 'alert') return null;
  const newest = synced
    .map((i) => i.last_synced_at!)
    .sort()
    .at(-1);
  if (!newest) return null;

  return (
    <div className="flex items-center justify-end gap-1.5 px-4 py-1">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500/70" />
      <span className="text-2xs text-ink-dim">
        Data updated {relTime(newest)} · {connected.length} source
        {connected.length === 1 ? '' : 's'} connected
      </span>
    </div>
  );
}
